import React, { useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import EditSiteModal from './EditSiteModal';
import MetricsChart from './MetricsChart';
import MetricsDashboard from './MetricsDashboard';

const SiteInformation = ({ site, formatDate, formatTimestamp, onSiteUpdate }) => {
    const { currentLang, fetchWithAuth, API_URL, getCsrfToken } = useAuth();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [checkingSite, setCheckingSite] = useState(false);
    const { user } = useAuth();

    const handleSiteUpdate = (updatedSite) => {
        console.log('SiteInformation: handleSiteUpdate called with:', updatedSite);
        if (onSiteUpdate) {
            onSiteUpdate(updatedSite);
        }
    };

    const handleCheckSite = async () => {
        try {
            setCheckingSite(true);
            const currentCsrfToken = await getCsrfToken();
            if (!currentCsrfToken) {
                throw new Error('Не удалось получить CSRF токен');
            }
            const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/check`, {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': currentCsrfToken,
                },
            });
            const data = await response.json();
            if (response.ok && data.success) {
                // Обновляем данные сайта
                if (onSiteUpdate) {
                    onSiteUpdate(null); // Перезагружаем данные
                }
            } else {
                console.error('Ошибка обновления сайта:', data.message);
            }
        } catch (error) {
            if (error.message === 'Требуется авторизация') {
                console.error('Сессия истекла при обновлении сайта');
            } else {
                console.error('Ошибка обновления сайта:', error);
            }
        } finally {
            setCheckingSite(false);
        }
};

  if (!site) return null;

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Заголовок с кнопками */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
        <h2 className="text-base sm:text-xl font-bold text-gray-800">
          {currentLang === 'ru' ? 'Информация о сайте' : 'Site Information'}
        </h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleCheckSite}
            className="px-2 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-1 sm:space-x-2 disabled:opacity-50 text-xs sm:text-sm"
            disabled={checkingSite}
            title={currentLang === 'ru' ? 'Обновить информацию по всем страницам сайта' : 'Update all site pages'}
          >
            {checkingSite ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1 sm:mr-2"></span>
                <span className="hidden sm:inline">{currentLang === 'ru' ? 'Обновление...' : 'Updating...'}</span>
                <span className="sm:hidden">{currentLang === 'ru' ? 'Обновление' : 'Updating'}</span>
              </span>
            ) : (
              <>
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">{currentLang === 'ru' ? 'Обновить информацию' : 'Update site information'}</span>
                <span className="sm:hidden">{currentLang === 'ru' ? 'Обновить' : 'Update'}</span>
              </>
            )}
          </button>
          {user?.role === 'admin' && (
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="px-2 sm:px-4 py-1.5 sm:py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="hidden sm:inline">{currentLang === 'ru' ? 'Редактировать' : 'Edit'}</span>
            <span className="sm:hidden">{currentLang === 'ru' ? 'Изменить' : 'Edit'}</span>
          </button>
          )}
          {site.seo_stat_url && site.seo_stat_url.trim() !== '' && (
            <a
              href={site.seo_stat_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-lg hover:from-green-500 hover:to-blue-600 transition-colors flex items-center justify-center space-x-1 sm:space-x-2 shadow-md text-xs sm:text-sm"
              title={currentLang === 'ru' ? 'Позиции сайта в поиске' : 'Site positions'}
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l6-6 4 4 8-8" />
              </svg>
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Позиции' : 'Positions'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Позиции' : 'Pos'}</span>
            </a>
          )}
        </div>
      </div>

      {/* График посещаемости из Яндекс.Метрики */}
      <MetricsChart 
          siteId={site.id}
          ymetricaId={site.ymetrica}
          currentLang={currentLang}
      />

      {/* Дополнительная аналитика из Яндекс.Метрики */}
      <MetricsDashboard 
          siteId={site.id}
          ymetricaId={site.ymetrica}
          currentLang={currentLang}
      />

      {/* Основная информация */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-4">{currentLang === 'ru' ? 'Основная информация' : 'Basic Information'}</h3>
          <div className="space-y-1.5 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">ID:</span>
              <span className="text-xs sm:text-sm font-medium">{site.id}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">DNS:</span>
              <span className="text-xs sm:text-sm font-medium break-all">{site.dns}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Статус:' : 'Status:'}</span>
              <span className={`px-1.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-bold rounded-full ${
                site.status === 1 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {site.status === 1 ? (currentLang === 'ru' ? 'Активен' : 'Active') : (currentLang === 'ru' ? 'Неактивен' : 'Inactive')}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">HTTPS:</span>
              <span className={`px-1.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-bold rounded-full ${
                site.https === 1 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {site.https === 1 ? 'HTTPS' : 'HTTP'}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Тариф:' : 'Tariff:'}</span>
              <span className={`px-1.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-bold rounded-full ${
                site.tarif === 1 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {site.tarif === 1 ? (currentLang === 'ru' ? 'Премиум' : 'Premium') : (currentLang === 'ru' ? 'Базовый' : 'Basic')}
              </span>
            </div>
          </div>
        </div>

        {/* WHOIS информация */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-4">{currentLang === 'ru' ? 'WHOIS информация' : 'WHOIS Information'}</h3>
          <div className="space-y-1.5 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Дата создания:' : 'Creation Date:'}</span>
              <span className="text-xs sm:text-sm font-medium">{formatDate(site.whois_dcr)}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Дата истечения:' : 'Expiration Date:'}</span>
              <span className="text-xs sm:text-sm font-medium">{formatDate(site.whois_exp)}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'SSL дата:' : 'SSL Date:'}</span>
              <span className="text-xs sm:text-sm font-medium">{formatTimestamp(site.ssl_date)}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">UDP:</span>
              <span className="text-xs sm:text-sm font-medium">{formatTimestamp(site.udp)}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">UCR:</span>
              <span className="text-xs sm:text-sm font-medium">{site.ucr ? (currentLang === 'ru' ? 'Да' : 'Yes') : (currentLang === 'ru' ? 'Нет' : 'No')}</span>
            </div>
          </div>
        </div>
        
        {/* Sitemap информация */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-4">Sitemap</h3>
          <div className="space-y-1.5 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Sitemap URL:' : 'Sitemap URL:'}</span>
              <a 
                href={site.sitemaps} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-xs sm:text-sm break-all"
              >
                {site.sitemaps}
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Ссылки в sitemap:' : 'Sitemap links:'}</span>
              <span className="text-xs sm:text-sm font-medium">{site.sitemaps_link}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Ошибки в sitemap:' : 'Sitemap errors:'}</span>
              <span className="text-xs sm:text-sm font-medium">{site.sitemaps_link_err}</span>
            </div>
          </div>
        </div>

        {/* Поисковые системы */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-4">{currentLang === 'ru' ? 'Поисковые системы' : 'Search Engines'}</h3>
          <div className="space-y-1.5 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Google ссылки:' : 'Google links:'}</span>
              <span className="text-xs sm:text-sm font-medium">{site.glink}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Yandex ссылки:' : 'Yandex links:'}</span>
              <span className="text-xs sm:text-sm font-medium">{site.ylink}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">Alexa Global Rank:</span>
              <span className="text-xs sm:text-sm font-medium">{site.alexa_gl || (currentLang === 'ru' ? 'Не указан' : 'Not specified')}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-0">
              <span className="text-xs sm:text-sm text-gray-600">{currentLang === 'ru' ? 'Яндекс.Метрика:' : 'Yandex Metrica:'}</span>
              <span className="text-xs sm:text-sm font-medium">
                {site.ymetrica && site.ymetrica !== '0' && String(site.ymetrica).trim() !== '' 
                  ? site.ymetrica 
                  : (currentLang === 'ru' ? 'Не указан' : 'Not specified')
                }
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Robots.txt */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-4">Robots.txt</h3>
          <div className="bg-gray-50 p-2 sm:p-4 rounded-lg font-mono text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap max-h-32 sm:max-h-48 overflow-y-auto">
            {site.robotstxt || (currentLang === 'ru' ? 'Файл robots.txt не найден' : 'robots.txt file not found')}
          </div>
        </div>
        
        {/* WHOIS данные */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-4">{currentLang === 'ru' ? 'WHOIS данные' : 'WHOIS Data'}</h3>
          <div className="bg-gray-50 p-2 sm:p-4 rounded-lg font-mono text-xs sm:text-sm overflow-x-auto max-h-32 sm:max-h-48 overflow-y-auto">
            {site.whois_text ? (
              site.whois_text.split('<br />').map((line, index) => (
                <div key={index} className="mb-0.5 sm:mb-1">
                  {line}
                </div>
              ))
            ) : (
              <div className="text-gray-500">{currentLang === 'ru' ? 'Информация отсутствует' : 'Information not available'}</div>
            )}
          </div>
        </div>
      </div>

      {/* Модальное окно редактирования */}
      <EditSiteModal
        site={site}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onUpdate={handleSiteUpdate}
      />
    </div>
  );
};

export default SiteInformation; 